import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { services } from '@/mocks/services';

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <section id="servicos" className="relative py-24 md:py-32 lg:py-40 bg-dark-900 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-premium-400 text-xs tracking-[0.3em] uppercase font-medium"
          >
            Nossos Serviços
          </motion.span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4">
            Excelência em{' '}
            <span className="text-gradient-gold">cada detalhe</span>
          </h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: '80px' } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="h-[1px] bg-premium-500 mx-auto mt-6"
          ></motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.08, ease: [0.4, 0, 0.2, 1] }}
              onMouseEnter={() => setHoveredId(service.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="group relative bg-dark-800 rounded-lg overflow-hidden border border-white/5 transition-all duration-500 hover:border-white/10"
            >
              <div className="relative h-48 overflow-hidden">
                <motion.img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover object-center transition-transform duration-700"
                  animate={{ scale: hoveredId === service.id ? 1.08 : 1 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-800 via-dark-800/40 to-transparent"></div>
                <div className="absolute top-3 right-3 bg-dark-950/80 backdrop-blur-sm px-2.5 py-1 rounded text-xs text-premium-400 font-medium">
                  {service.duration}
                </div>
              </div>

              <div className="p-5 md:p-6">
                <div className="flex items-start gap-3 mb-3">
                  <motion.div
                    animate={hoveredId === service.id ? { rotate: 15 } : { rotate: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-9 h-9 rounded-md bg-premium-600/20 flex items-center justify-center flex-shrink-0 mt-0.5"
                  >
                    <i className={`${service.icon} text-premium-400 text-sm`}></i>
                  </motion.div>
                  <div>
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-white mb-1">
                      {service.name}
                    </h3>
                    <p className="text-white/50 text-xs md:text-sm leading-relaxed line-clamp-2">
                      {service.description}
                    </p>
                  </div>
                </div>

                <div className="relative pt-4 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-premium-400 font-heading text-lg md:text-xl font-bold">
                      {service.price}
                    </span>
                    <span className="text-white/40 text-xs">{service.duration}</span>
                  </div>
                  <motion.div
                    className="absolute bottom-0 left-0 h-[1px] bg-premium-500"
                    initial={{ width: 0 }}
                    animate={{ width: hoveredId === service.id ? '100%' : '0%' }}
                    transition={{ duration: 0.4 }}
                  ></motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}